import React, { useRef, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ModalBody } from 'reactstrap';
import { useTranslation } from 'react-i18next';

import useApi from 'hooks/useApi';
import useDocumentation from 'hooks/useDocumentation';

import {
  getDocumentPath,
  setNavigationPane,
  refreshPath,
  setSelectNewDocument,
  setDocumentsView,
} from 'redux/actions/documentActions';
import { saveAlert } from 'redux/actions/notificationActions';
import { addBreadcrumb, removeBreadcrumb } from 'redux/actions/layoutActions';
import { selectCurrentClient } from 'redux/actions/clientActions';

import { ReactComponent as DocumentIcon } from 'assets/icon-document.svg';

import { LabelIcon } from 'shared/components/Labels';
import { colors } from 'shared/defaultTheme';
import { ModalHeader } from 'shared/components/modal/ModalHeaders';
import Contacts from 'shared/components/DocumentForm/Contacts';
import AlertModal from 'shared/components/AlertModal';
import ConfirmModal from 'shared/components/ConfirmModal';
import { Checkbox } from 'shared/components/form/CheckBox';
import MainSpinner from 'shared/components/MainSpinner';
import { ContactsModal } from 'shared/components/SharedStyles/SharedStyles';

import { DOCUMENTS_VIEW } from 'shared/constants/variants';
import FileManager from './components/FileManager';
import ClientsComponent from './components/Clients';
import UploadDocumentClients from './components/UploadDocumentClients';
import NotifiForm from './components/NotifiForm';
import { DocumentWrapper, DocumentTableBackground, NotifiFormModal } from './styles';

const Documentations = () => {
  const { customAreaApi } = useApi();
  const theme = useSelector((state) => state.theme);
  const dispatch = useDispatch();
  const { pathname, search } = useLocation();
  const documentState = useSelector((state) => state.document);
  const notificationState = useSelector((state) => state.notification);
  const { breadcrumbs } = useSelector((state) => state.layout);
  const { isCustomer, cusNavId, clientId } = useSelector((state) => state.auth);
  const { currentClient } = useSelector((state) => state.clientState);
  const { t } = useTranslation();
  const refMenu = useRef(null);
  const refFileManager = useRef(null);
  const { mainColor } = theme;
  const [path, setPath] = useState();
  const [openFormModal, setOpenFormModal] = useState(false);
  const [refreshFolder, setRefreshFolder] = useState(false);
  const [showSuccessUpload, setShowSuccessUpload] = useState();
  const [showMessage, setShowMessage] = useState(false);
  const [showError, setShowError] = useState(false);
  const [messageErrorDownload, setMessageError] = useState('');
  const [openContactsModal, setOpenContactsModal] = useState(false);
  const [loadingCreateFolder, setLoadingCreateFolder] = useState(false);
  const [selectedClients, setSelectedClients] = useState([]);
  const [showMultipleSelect, setMultipleSelect] = useState(false);
  const [showConfirmUnPublic, setShowConfirmUnPublic] = useState(false);
  const [isInputFile, setInputFile] = useState(false);

  const onToggleSuccessUpload = () => setShowSuccessUpload((currentShow) => !currentShow);

  const onOkSuccessUpload = () => {
    onToggleSuccessUpload();
  };

  const onToggleError = () => setShowError((currentShow) => !currentShow);

  const {
    handlePopUpClose,
    handleMenuClick,
    elementEmptyTable,
    elementEmptyLargeIcon,
    loadingDownload,
    handleToolbarCreate,
    handleActionAddClick,
    handleToolbarClick,
    handlePopupOpen,
    handleMenuOpen,
    onOkUnPublic,
    loadingUnPublic,
    onCloseContactsModal,
    onCancelAction,
    onCloseFormModal,
  } = useDocumentation({
    refFileManager,
    setOpenFormModal,
    setOpenContactsModal,
    setShowConfirmUnPublic,
    setMessageError,
    onToggleError,
    setInputFile,
  });

  const [, getClient] = customAreaApi(
    {
      method: 'GET',
    },
    { manual: true }
  );

  const clintData = async () => {
    const {
      data: { data },
    } = await getClient({
      url: `${window.API_V1}/cli/clients/${cusNavId}/${clientId}`,
    });
    dispatch(selectCurrentClient(data));
  };

  useEffect(() => {
    dispatch(setDocumentsView(DOCUMENTS_VIEW.ROOT));
    if (clientId) {
      clintData();
    }
    return () => {
      dispatch(setDocumentsView(''));
    };
  }, []);

  useEffect(() => {
    if (search && search.length) {
      const params = new URLSearchParams(search);
      const pathParam = params.get('path');
      setPath(pathParam);
    }
  }, [search]);

  useEffect(() => {
    if (breadcrumbs.length > 1) {
      breadcrumbs.forEach(() => {
        dispatch(removeBreadcrumb());
      });
      dispatch(
        addBreadcrumb({
          active: false,
          label: 'general_home',
          link: '/home',
          isModify: false,
        })
      );
    }
    dispatch(
      addBreadcrumb({
        label: 'general_pagination_documents',
        active: true,
        link: 'documentations-client',
        isModify: !isCustomer,
      })
    );
  }, []);

  useEffect(() => {
    if (currentClient) {
      dispatch(
        addBreadcrumb({
          label: currentClient?.clientName,
          active: true,
          link: pathname,
          isModify: false,
        })
      );
    }
  }, [currentClient]);

  useEffect(() => {
    if (!documentState.isNavigationPane && !documentState.isRefreshPath) {
      dispatch(setNavigationPane(true));
      refFileManager.current?.refresh();
    }
    if (documentState.isRefreshPath) {
      dispatch(setNavigationPane(true));
      dispatch(refreshPath(false));
    }
  }, [documentState.isNavigationPane, documentState.isRefreshPath]);

  useEffect(() => {
    if (documentState.path && documentState.readDocument) {
      refFileManager.current.path = documentState.path;
      dispatch(getDocumentPath(null));
    }
    if (documentState.readDocument && path) {
      refFileManager.current.path = path;
      setPath(null);
    }
  }, [documentState.readDocument, path]);

  useEffect(() => {
    if (notificationState.isSaveAlert) {
      refFileManager.current?.refresh();
      dispatch(saveAlert(false));
    }
  }, [notificationState.isSaveAlert]);

  useEffect(() => {
    if (documentState.failure) {
      const content = document?.querySelector('.e-dlg-body-error');
      const messageError = content?.querySelector('.e-fe-errorcontent');
      const { action, error } = documentState.failure;
      if (action === 'read' && error.code === '404') {
        messageError.innerHTML = t('documents_not_found_client');
      }
    }
  }, [documentState.failure]);

  useEffect(() => {
    if (documentState.readDocument) {
      setTimeout(() => setRefreshFolder(true), 500);
    }
  }, [documentState.readDocument]);

  useEffect(() => {
    if (refreshFolder) {
      setRefreshFolder(false);
      elementEmptyTable();
      elementEmptyLargeIcon();
    }
  }, [refreshFolder]);

  const onCancelNewDocument = () => {
    if (showMessage) {
      document.cookie = 'showMessageAgain=true';
    }
    dispatch(setSelectNewDocument(false));
  };

  const onOkNewDocument = () => {
    if (showMessage) {
      document.cookie = 'showMessageAgain=true';
    }
    dispatch(setSelectNewDocument(false));
  };

  return (
    <DocumentWrapper className="pb-3">
      {isCustomer && (
        <ClientsComponent
          selectedClients={selectedClients}
          setSelectedClients={setSelectedClients}
          setMultipleSelect={setMultipleSelect}
          showMultipleSelect={showMultipleSelect}
        />
      )}
      <DocumentTableBackground
        className="mr-2 background-box__flex-documentations background-box__file-manager-document"
        isCustomer={isCustomer}
        isCollapse={documentState.isCollapse}
        key={`${currentClient?.clientId}`}
      >
        <LabelIcon
          label={t('general_documentation')}
          Icon={() => <DocumentIcon className="label-icon__document" fill={colors.white} />}
          color={mainColor}
          className="ml-1 label-icon text-uppercase"
          classNameMain="mb-3"
        />
        <AlertModal
          type="success"
          isOpen={showSuccessUpload}
          toggle={onOkSuccessUpload}
          className="alert-success-z"
          message={t('documents_success_upload_file')}
          okText={t('general_button_ok')}
          onOk={onOkSuccessUpload}
        />
        {showMultipleSelect ? (
          <UploadDocumentClients
            setSelectedClients={setSelectedClients}
            selectedClients={selectedClients}
          />
        ) : (
          <FileManager
            onToolbarCreate={handleToolbarCreate}
            onToolbarClick={handleToolbarClick}
            onMenuClick={handleMenuClick}
            onMenuOpen={handleMenuOpen}
            onActionAddClick={handleActionAddClick}
            onPopupOpen={handlePopupOpen}
            onPopUpClose={handlePopUpClose}
            refMenu={refMenu}
            refFileManager={refFileManager}
            onToggleSuccessUpload={onToggleSuccessUpload}
            setLoadingCreateFolder={setLoadingCreateFolder}
            loadingCreateFolder={loadingCreateFolder}
            isInputFile={isInputFile}
            setInputFile={setInputFile}
          />
        )}
      </DocumentTableBackground>
      {isCustomer && (
        <ConfirmModal
          isOpen={documentState.openNewDocument}
          toggle={onCancelNewDocument}
          confirmMessage={t('documents_new_document_message_info')}
          okText={t('general_button_ok')}
          onOk={onOkNewDocument}
        >
          <div className="d-flex justify-content-center">
            <Checkbox
              checked={showMessage}
              onClick={(event) => {
                const { target } = event;
                const { checked } = target;
                setShowMessage(checked);
              }}
            />
            <span className="no-show-message">{t('general_no_show_message_again')}</span>
          </div>
        </ConfirmModal>
      )}
      <NotifiFormModal isOpen={openFormModal} toggle={onCloseFormModal} size="md">
        <ModalHeader
          className="modal-header"
          backgroundColor={colors.transparent}
          onClose={onCloseFormModal}
        />
        <ModalBody>
          <NotifiForm onCancel={onCloseFormModal} />
        </ModalBody>
      </NotifiFormModal>
      <ContactsModal isOpen={openContactsModal} toggle={onCloseContactsModal} size="md" top="15%">
        <ModalHeader
          className="modal-header"
          backgroundColor={colors.transparent}
          onClose={onCloseContactsModal}
        />
        <ModalBody>
          <Contacts onCancel={onCloseContactsModal} />
        </ModalBody>
      </ContactsModal>
      <AlertModal
        type="error"
        isOpen={showError}
        toggle={onToggleError}
        message={messageErrorDownload}
        okText={t('general_button_ok')}
        onOk={onToggleError}
      />
      <ConfirmModal
        isOpen={showConfirmUnPublic}
        toggle={onCancelAction}
        confirmMessage={t('document_un_public_confirm')}
        cancelText={t('general_discard')}
        okText={t('general_button_ok')}
        onCancel={onCancelAction}
        onOk={onOkUnPublic}
      />
      {(loadingDownload || loadingCreateFolder || loadingUnPublic) && <MainSpinner />}
    </DocumentWrapper>
  );
};

export default Documentations;
